import { designVariables } from "@studio3t/design-variables";
import { styledTableClassNames } from "@studio3t/generic-components";
import styled from "styled-components";

// The list pages' table chrome (Figma Content area 13349-39128): the card,
// the fixed-layout table with sticky headers, rows, cells and the empty
// message — shared by the Pipelines tabs. Widths come from each table's
// colgroup; these carry only the paint and the metrics.
const SINGLE_LINE = designVariables.type.singleLine;
const SEMANTIC = designVariables.ui.semantic;
const TABLE = designVariables.ui.table;
const CARDS = designVariables.ui.cards;
const CARD_RADIUS = CARDS.corner;

// Fills the scrollable page container so the table can scroll internally
// while the filter bar and pagination stay visible.
export const PageRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

export const TableCard = styled.div`
  background: ${(props) => props.theme.cards1Background};
  border: 1px solid ${(props) => props.theme.cards1Border};
  border-radius: ${CARD_RADIUS}px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;

  /* StyledTableScroll's frame draws its own border and stretches to 100%
   * height; the card already owns the border, and the frame should hug the
   * rows so a short table doesn't leave a gap above the pagination bar. */
  .${styledTableClassNames.scroll} {
    border: none;
    height: auto;
    min-height: 0;
  }

  /* Keep the vertical scrollbar below the sticky header row. !important is
   * required to beat overlayscrollbars' own positioning rules (same as
   * common-ui's DataTable does). */
  .os-scrollbar-vertical {
    top: ${TABLE.thead.cell.height + 1}px !important;
  }
`;

export const Table = styled.table`
  width: 100%;
  /* Sum of readable column minimums; below this the card scrolls
     horizontally instead of squashing columns. The dominant term is the
     Pipeline column: the worst case is bounded ("SRC x → MW y +N → SINK z
     +N") and needs ~560px with every label at its 80px minimum (see
     StageText) before ellipsing. */
  min-width: 1200px;
  /* separate, not collapse: collapsed borders detach from position: sticky
     headers and scroll away with the body */
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  background: ${(props) => props.theme.tableTheadCellBackground};

  tbody tr:last-child td {
    border-bottom: none;
  }

  th:last-child,
  td:last-child {
    border-right: none;
  }
`;

export const TableHead = styled.thead`
  background: ${(props) => props.theme.tableTheadCellBackground};

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${(props) => props.theme.tableTheadCellBackground};
  }
`;

export const TableRow = styled.tr`
  cursor: pointer;

  &:hover {
    background: ${(props) => props.theme.tableTbodyCellHoverBackground};
  }

  /* Rows are keyboard-focusable (Enter/Space opens the pipeline). The
     browser-default focus ring is kept; only inset it so the scroll
     container doesn't clip it. */
  &:focus-visible {
    outline-offset: -${SEMANTIC.focusstroke}px;
  }
`;

export const TableHeader = styled.th`
  text-align: left;
  height: ${TABLE.thead.cell.height}px;
  padding: 0 ${TABLE.thead.cell.paddingr}px 0 ${TABLE.thead.cell.paddingl}px;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  font-weight: ${SINGLE_LINE.small.regular.fontWeight};
  color: ${(props) => props.theme.semanticTextLight};
  white-space: nowrap;
  border-bottom: 1px solid ${(props) => props.theme.tableBackground};
  border-right: 1px solid ${(props) => props.theme.tableBackground};

  /* Column separators are the gaps *between* columns (Figma 13349-39133):
     the last column meets the card edge without its own line, which would
     double with the card border. */
  &:last-child {
    border-right: none;
  }
`;

export const TableCell = styled.td`
  height: ${TABLE.tbody.cell.heightm}px;
  padding: 0 ${TABLE.tbody.cell.padding}px;
  font-size: ${SINGLE_LINE.default.regular.fontSize}px;
  color: ${(props) => props.theme.semanticTextBody};
  vertical-align: middle;
  border-bottom: 1px solid ${(props) => props.theme.tableBackground};
  border-right: 1px solid ${(props) => props.theme.tableBackground};
  overflow: hidden;
`;

// Figma nests cell content in a wrapper with table/tbody/cell/gap (6px)
// horizontal padding on top of the 4px cell padding.
export const CellText = styled.span`
  display: block;
  padding: 0 ${TABLE.tbody.cell.gap}px;
  color: ${(props) => props.theme.semanticTextBody};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ActionsCell = styled.td`
  height: ${TABLE.tbody.cell.heightm}px;
  padding: 0 ${TABLE.tbody.cell.padding}px;
  text-align: center;
  vertical-align: middle;
  border-bottom: 1px solid ${(props) => props.theme.tableBackground};
`;

export const EmptyRowMessage = styled.div`
  /* Figma: semantic/spacingxl (40px) — token not yet in @studio3t/design-variables */
  padding: 40px 0;
  text-align: center;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  font-style: italic;
  color: ${(props) => props.theme.semanticTextLight};
`;

// Badges hug their content and sit at the cell's text inset: the badge
// component renders as a block-level flex container by default, so the
// slot forces it inline.
export const BadgeSlot = styled.div`
  padding-left: ${TABLE.tbody.cell.gap}px;

  & > * {
    display: inline-flex;
  }
`;
